import React, {ReactElement, useEffect, useRef, useState} from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {uuid} from '../../../../utils/utils';
import EventSequenceService from '../../../../services/event-sequence.service';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {LintCheck, LintSeverity, ModelLintFinding, ModelLintSnapshot} from '../../../../@types/model-quality';
import {getModelTestingCache} from './modelTestingCache';
import {TestingScope, lintSnapshotForScope, scopeKey, startLintForScope} from './testing-scope';

const POLL_INTERVAL_MS = 1500;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

const SEVERITY_ORDER: LintSeverity[] = ['ERROR', 'WARNING', 'INFO'];
const SEVERITY_COLOR: Record<LintSeverity, 'error' | 'warning' | 'info'> = {
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

const SEVERITY_HELP: Record<LintSeverity, string> = {
    ERROR: 'These will actively degrade AI report mapping. Fix them before relying on mapping results for this model.',
    WARNING: 'These are likely to reduce mapping quality. Review each one and fix it where the concern really applies.',
    INFO: 'Observations worth knowing about. They often reflect intentional modelling choices; act only where the note matches a real gap.',
};

/** Plain-language explanation of each check: what it means and what to do about it. */
const CHECK_HELP: Record<LintCheck, string> = {
    MISSING_STATEMENT:
        'This node has no statement of truth, so the AI can only match reports against the node name, and the '
        + 'safety checks that verify mappings against statements cannot run here. Write a statement describing '
        + 'exactly when this node is true.',
    AND_GATE_WITHOUT_PRECURSOR_CHILD:
        'An AND gate normally combines one precursor (the state that occurred) with the controls that failed to '
        + 'stop it. Without a precursor child, the gate arithmetic has nothing to escalate from. Check the gate structure.',
    AND_GATE_MULTIPLE_PRECURSOR_CHILDREN:
        'An AND gate with several precursor children is ambiguous for the mapping arithmetic, which expects a '
        + 'single causal path plus controls. Consider restructuring.',
    OR_GATE_WITH_BARRIER_CHILDREN:
        'Automatic escalation through OR gates only follows precursor children; barrier children are never escalated '
        + 'through. This is usually intentional (a decomposition of how a control fails), so just confirm the intent.',
    BARRIER_WITHOUT_FAIL_CRITERIA:
        'The statement does not spell out when this control counts as failed versus successful ("This control fails '
        + 'if..."). The AI has to infer the held or failed judgment from the wording alone, which is less reliable.',
    OR_GATE_WITHOUT_CATCH_ALL:
        'When a report confirms this gate but does not name a specific child reason, it is dropped to "unspecified" '
        + 'and the reason is lost. An "other reason" catch-all child captures both "it was something else" and "the '
        + 'report does not explain why", so the arrest is counted and you can act on it.',
    GATE_STATEMENT_MISSING_AND_SEMANTICS:
        'The gate statement can be read as satisfied even when its controls succeeded, so mapping may escalate past '
        + 'a successful control. Add the control-failure requirement to the statement (compare the NMAC convention: '
        + '"All attempts to resolve the conflict failed").',
    STATEMENT_OUT_OF_SCOPE:
        'A report from outside this event sequence\'s scope could satisfy the statement as written; the detail shows '
        + 'an AI-generated example. Add a qualifier that excludes it (for example "airborne").',
    SIBLING_STATEMENTS_OVERLAP:
        'Two sibling statements could both be satisfied by the same incident. Under an OR gate that can be fine '
        + '(several children may genuinely apply); if the children are meant to be exclusive, tighten the wording.',
    PARENT_CHILD_BOUNDARY_AMBIGUOUS:
        'It is unclear whether the parent state or only the child state applies when the controls between them '
        + 'succeed, so mapping may park reports at the wrong level. Clarify the boundary in the two statements.',
    NEGATED_STATE_STATEMENT:
        'This statement is phrased as something that did NOT happen ("...not resolved", "has not been avoided"). '
        + 'The AI cannot confirm a negative from a report that is silent, and it reads a worse actual outcome such '
        + 'as a collision as making the statement false, so the state fails its check exactly when it is most true. '
        + 'Left unfixed this produces false control-success credits on real accidents.',
    PRECURSOR_NOT_PAIRED_WITH_ONE_CONTROL:
        'A precursor should sit under a gate with exactly one generalised control, so that when the parent state did '
        + 'not occur the resulting success has one clear place to go. Zero or several controls make that attribution '
        + 'ambiguous.',
    CIRCUMSTANTIAL_BESIDE_PRECURSOR:
        'Circumstantial factors describe a way the control succeeds or fails, so they belong under the control, not '
        + 'directly beside the precursor where they compete with it as separate controls.',
    CONTROL_STATEMENT_NEGATED_HELD_STATE:
        'The held state of this control is described as an absence ("...no other aircraft occupied..."). The AI '
        + 'reads a "present" observation as the named hazard being in the report, so the control gets credited as '
        + 'held exactly when the report shows the hazard, and marked failed when the report shows none. Both '
        + 'directions of this control\'s pass/fail statistics invert.',
    CONTROL_STATEMENT_SILENCE_MEANS_FAILED:
        'The statement says to treat this control as failed when the report does not mention it. Mapping '
        + 'follows that instruction, so every report that is silent about this control counts it as failed and '
        + 'can be escalated past it into a more serious state the report never describes.',
    STATEMENT_RESTATES_ANCESTORS:
        'This statement repeats wording from a statement above it. The AI is only asked about this node when the '
        + 'branch above it was already taken, and it is given the causal chain and the parent statement as part of '
        + 'the question, so the repeated part has to be confirmed all over again from the same report for no gain. '
        + 'Every extra condition is another chance for a silent report to turn the answer false.',
    STATEMENT_TOO_COMPLEX:
        'This statement joins a lot of conditions with "because", "and", "or" and carve-outs. The AI answers it as '
        + 'a single true or false, so every condition has to resolve the same way for that answer to mean anything, '
        + 'and one condition the report happens not to mention decides the whole statement.',
    STATEMENT_VERBOSE:
        'This statement is long. Length on its own is not a fault and some states genuinely need the words, but a '
        + 'statement this size usually carries clauses that are not part of the risk state it is asserting.',
    STATEMENT_HARD_TO_JUDGE:
        'Asked of the AI directly: could a careful reader answer true or false for this statement from one report '
        + 'without guessing at something the report is silent on? It answered no, and the detail names the clause '
        + 'it would drop.',
    UNCLASSED_NODE_IN_CONTROL_POSITION:
        'This node sits beside or under a precursor without a class. The mapping treats every non-precursor '
        + 'child of a gate as a control and asks the AI whether it held or failed, but only a barrier can '
        + 'hold and only a circumstance can be present, so the verdict on this node means nothing. It is '
        + 'still reported as a control in shakedowns, and it can still feed where a report lands.',
};

const CHECK_LABELS: Record<LintCheck, string> = {
    MISSING_STATEMENT: 'Missing statement of truth',
    AND_GATE_WITHOUT_PRECURSOR_CHILD: 'AND gate without a precursor child',
    AND_GATE_MULTIPLE_PRECURSOR_CHILDREN: 'AND gate with multiple precursor children',
    OR_GATE_WITH_BARRIER_CHILDREN: 'OR/XOR gate with barrier children',
    BARRIER_WITHOUT_FAIL_CRITERIA: 'Barrier statement without held/fail criteria',
    OR_GATE_WITHOUT_CATCH_ALL: 'OR gate without a catch-all child',
    GATE_STATEMENT_MISSING_AND_SEMANTICS: 'Statement omits the gate\'s own AND semantics',
    STATEMENT_OUT_OF_SCOPE: 'Statement satisfiable by out-of-scope incidents',
    SIBLING_STATEMENTS_OVERLAP: 'Sibling statements may overlap',
    PARENT_CHILD_BOUNDARY_AMBIGUOUS: 'Parent/child boundary ambiguous when controls succeed',
    NEGATED_STATE_STATEMENT: 'Statement is phrased as a negated outcome',
    PRECURSOR_NOT_PAIRED_WITH_ONE_CONTROL: 'Precursor not paired with exactly one control',
    CIRCUMSTANTIAL_BESIDE_PRECURSOR: 'Circumstantial factor sits beside a precursor',
    CONTROL_STATEMENT_NEGATED_HELD_STATE: 'Control held state is phrased as an absence',
    CONTROL_STATEMENT_SILENCE_MEANS_FAILED: 'Control is treated as failed when not mentioned',
    STATEMENT_RESTATES_ANCESTORS: 'Statement repeats its ancestors',
    STATEMENT_TOO_COMPLEX: 'Statement joins too many conditions',
    STATEMENT_VERBOSE: 'Statement is long',
    STATEMENT_HARD_TO_JUDGE: 'Statement may not be answerable from one report',
    UNCLASSED_NODE_IN_CONTROL_POSITION: 'Unclassed node judged as a control',
};

/** Concrete, imperative remediation for each check, shown as "How to fix". */
const CHECK_FIX: Record<LintCheck, string> = {
    MISSING_STATEMENT:
        'Write a statement of truth describing exactly the condition this node represents, so reports can be '
        + 'matched and verified against it.',
    AND_GATE_WITHOUT_PRECURSOR_CHILD:
        'Add a PRECURSOR child representing the state this gate demotes to when a control here succeeds.',
    AND_GATE_MULTIPLE_PRECURSOR_CHILDREN:
        'Keep one PRECURSOR child as the causal chain; move the others under an OR gate or reclassify them.',
    OR_GATE_WITH_BARRIER_CHILDREN:
        'Confirm this is intended, or move the barrier under the precursor it defends so elevation is not blocked.',
    BARRIER_WITHOUT_FAIL_CRITERIA:
        'Add explicit fail criteria to the statement, for example "This control fails if ...", so held versus failed '
        + 'is unambiguous.',
    OR_GATE_WITHOUT_CATCH_ALL:
        'Add an "other reason" catch-all child so reports that do not name the sub-reason have somewhere to land.',
    GATE_STATEMENT_MISSING_AND_SEMANTICS:
        'Add the control-failure requirement to the statement, for example "All attempts to resolve the conflict '
        + 'failed", so it is not satisfied when a control succeeded.',
    STATEMENT_OUT_OF_SCOPE:
        'Add a qualifier that excludes the out-of-scope incident (for example "airborne"), or confirm it belongs in '
        + 'this event sequence.',
    SIBLING_STATEMENTS_OVERLAP:
        'If the siblings are meant to be exclusive, tighten each statement so one incident cannot satisfy both.',
    PARENT_CHILD_BOUNDARY_AMBIGUOUS:
        'Add the control-failure requirement to the parent statement so it is not read as satisfied when the child '
        + 'state was resolved by a control.',
    NEGATED_STATE_STATEMENT:
        'Reword the statement to say what DID happen. Lead with the outcome ("a collision occurred, or ..."), and '
        + 'for a control that reports often omit, add "if the report does not mention X, treat this as TRUE".',
    PRECURSOR_NOT_PAIRED_WITH_ONE_CONTROL:
        'Pair the precursor with exactly one generalised control, and model the specific mechanisms as children of '
        + 'that single control.',
    CIRCUMSTANTIAL_BESIDE_PRECURSOR:
        'Move the circumstantial factor(s) to be children of the generalised control, not direct siblings of the '
        + 'precursor.',
    CONTROL_STATEMENT_NEGATED_HELD_STATE:
        'Rephrase the held state as a positive fact about the hazard (for example "Another aircraft, vehicle, or '
        + 'person was also using the runway"), keeping the "This control fails if..." clause consistent with it.',
    CONTROL_STATEMENT_SILENCE_MEANS_FAILED:
        'Delete the sentence about reports that do not mention this control. Keep the "This control fails if..." '
        + 'criteria.',
    STATEMENT_RESTATES_ANCESTORS:
        'Cut the repeated clause and leave only what this node adds to the state above it. The chain above is '
        + 'already given to the AI with the question.',
    STATEMENT_TOO_COMPLEX:
        'Keep the one condition this node asserts and move the rest where they belong: a separate state gets its '
        + 'own node, and a carve-out only earns its place if a sibling would otherwise collide with it.',
    STATEMENT_VERBOSE:
        'Read it back and cut what a reader would not need in order to answer true or false: restated context, '
        + 'the cause of the state, and the outcome of any control.',
    STATEMENT_HARD_TO_JUDGE:
        'Remove the clause named in the detail, then read the statement back and check it can still be answered '
        + 'true or false from a single report.',
    UNCLASSED_NODE_IN_CONTROL_POSITION:
        'Give this node a class: BARRIER if it is a safeguard that can hold or fail, CIRCUMSTANTIAL if it is '
        + 'a condition that can be present or absent, or PRECURSOR if it is a state that occurred.',
};

/** Statement-based findings where "Suggest a fix" can call the statement-of-truth generator. */
const AI_FIXABLE: Set<LintCheck> = new Set<LintCheck>([
    'MISSING_STATEMENT', 'NEGATED_STATE_STATEMENT', 'STATEMENT_OUT_OF_SCOPE',
    'CONTROL_STATEMENT_NEGATED_HELD_STATE', 'CONTROL_STATEMENT_SILENCE_MEANS_FAILED', 'STATEMENT_RESTATES_ANCESTORS',
    'STATEMENT_TOO_COMPLEX', 'STATEMENT_VERBOSE', 'STATEMENT_HARD_TO_JUDGE',
]);

/**
 * Checks whose detail names text to cut out of the statement rather than a case the statement
 * must exclude. The two read alike in the panel but mean opposite things to the generator.
 */
const DETAIL_IS_TEXT_TO_CUT: Set<LintCheck> = new Set<LintCheck>(['STATEMENT_HARD_TO_JUDGE']);

/**
 * The revision request handed to the generator so it repairs the specific flaw rather than
 * regenerating blind. A missing statement has nothing to repair, so it regenerates from scratch.
 */
function refinementFor(finding: ModelLintFinding): string | undefined {
    if (finding.check === 'MISSING_STATEMENT') {
        return undefined;
    }
    const fix = CHECK_FIX[finding.check];
    if (!finding.detail) {
        return fix;
    }
    return DETAIL_IS_TEXT_TO_CUT.has(finding.check)
        ? `${fix} Specifically, remove this: ${finding.detail}`
        : `${fix} Specifically, exclude this case: ${finding.detail}`;
}

interface FixState {
    loading: boolean;
    suggestion?: string;
    error?: string;
}

/**
 * Static model quality review panel, hosted as the "Review" tab of the Model
 * Testing modal. Runs on demand (explicit button, since the LLM micro-checks
 * cost real compute); findings are tabbed by severity and grouped per check.
 */
export default function ModelLintPanel(
    props: { scope: TestingScope | null }): ReactElement {
    const {scope} = props;
    const cacheKey = scope ? scopeKey(scope) : null;
    const {canvasNavigateFunction} = useWorkspaceContext();
    const [snapshot, setSnapshot] = useState<ModelLintSnapshot | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [started, setStarted] = useState<boolean>(false);
    const [severityTab, setSeverityTab] = useState<number>(0);
    const [fixState, setFixState] = useState<Record<number, FixState>>({});
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeRunIdRef = useRef<string | null>(null);

    const handleSuggestFix = async (finding: ModelLintFinding): Promise<void> => {
        if (finding.nodeId == null || finding.parentId == null || finding.nodeName == null
            || finding.nodeClass == null || finding.gateType == null) {
            return;
        }
        const nodeId = finding.nodeId;
        setFixState(prev => ({...prev, [nodeId]: {loading: true}}));
        try {
            const suggestion = await EventSequenceService.suggestStatementOfTruth({
                nodeId,
                parentId: finding.parentId,
                name: finding.nodeName,
                nodeClass: finding.nodeClass,
                gateType: finding.gateType,
                refinement: refinementFor(finding),
            });
            setFixState(prev => ({...prev, [nodeId]: {loading: false, suggestion}}));
        } catch (err) {
            setFixState(prev => ({
                ...prev,
                [nodeId]: {loading: false, error: err instanceof Error ? err.message : 'Suggestion failed'},
            }));
        }
    };

    const stopPolling = (): void => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        activeRunIdRef.current = null;
    };

    useEffect((): (() => void) => stopPolling, []);

    // Restore the most recent analysis for this model (finished results reappear,
    // an in-flight run resumes polling); the user replaces it explicitly via Re-run.
    useEffect((): void => {
        if (scope == null || cacheKey == null) {
            return;
        }
        const cached = getModelTestingCache(cacheKey).lint;
        if (!cached) {
            return;
        }
        setStarted(true);
        setSnapshot(cached.snapshot);
        setErrorMessage(cached.errorMessage);
        if (cached.snapshot != null && !cached.snapshot.done && cached.errorMessage == null) {
            activeRunIdRef.current = cached.runId;
            void poll(scope, cached.runId, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey]);

    const poll = async (target: TestingScope, theRunId: string, consecutiveErrors: number): Promise<void> => {
        if (activeRunIdRef.current !== theRunId) {
            return;
        }
        try {
            const snap = await lintSnapshotForScope(target, theRunId);
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            setSnapshot(snap);
            getModelTestingCache(scopeKey(target)).lint =
                {runId: theRunId, snapshot: snap, errorMessage: snap.error ?? null};
            if (snap.error) {
                setErrorMessage(snap.error);
                stopPolling();
                return;
            }
            if (snap.done) {
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(() => { void poll(target, theRunId, 0); }, POLL_INTERVAL_MS);
        } catch (error) {
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            if (consecutiveErrors + 1 >= MAX_CONSECUTIVE_POLL_ERRORS) {
                setErrorMessage(error instanceof Error ? error.message : 'Lint polling failed');
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(
                () => { void poll(target, theRunId, consecutiveErrors + 1); }, POLL_INTERVAL_MS);
        }
    };

    const startLint = (): void => {
        if (scope == null || cacheKey == null) {
            return;
        }
        stopPolling();
        setSnapshot(null);
        setErrorMessage(null);
        setSeverityTab(0);
        setFixState({});
        setStarted(true);
        const runId = uuid();
        activeRunIdRef.current = runId;
        getModelTestingCache(cacheKey).lint = {runId, snapshot: null, errorMessage: null};
        startLintForScope(scope, {runId})
            .then(() => { void poll(scope, runId, 0); })
            .catch((error: Error) => {
                setErrorMessage(error.message);
                getModelTestingCache(cacheKey).lint = {runId, snapshot: null, errorMessage: error.message};
                stopPolling();
            });
    };

    const findings = snapshot?.findings ?? [];
    const running = started && errorMessage == null && (snapshot == null || !snapshot.done);
    const finished = snapshot?.done === true || errorMessage != null;
    const progress = snapshot && snapshot.llmChecksTotal > 0
        ? (snapshot.llmChecksDone / snapshot.llmChecksTotal) * 100
        : null;

    const bySeverity = (severity: LintSeverity): ModelLintFinding[] =>
        findings.filter(f => f.severity === severity);

    const renderCheckGroup = (check: LintCheck, group: ModelLintFinding[]): ReactElement => (
        <Accordion key={check} disableGutters defaultExpanded={group.length <= 5}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight="bold">
                        {CHECK_LABELS[check] ?? 'Model quality issue'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">({group.length})</Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{pt: 0}}>
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    {CHECK_HELP[check]}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                    {group[0].message}
                </Typography>
                <Typography variant="body2" sx={{mb: 1}}>
                    <Box component="span" fontWeight="bold">How to fix: </Box>
                    {CHECK_FIX[check]}
                </Typography>
                {group.map((finding: ModelLintFinding, index: number): ReactElement => {
                    const fixable = AI_FIXABLE.has(finding.check)
                        && finding.nodeId != null && finding.parentId != null;
                    const fx = finding.nodeId != null ? fixState[finding.nodeId] : undefined;
                    const navigate = finding.nodeId != null && canvasNavigateFunction != null
                        ? (): void => canvasNavigateFunction(`ft-${finding.nodeId}`)
                        : undefined;
                    return (
                        <Box key={index} sx={{borderTop: '1px solid', borderColor: 'divider', py: 0.75}}>
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                onClick={navigate}
                                title={navigate ? 'Open this node in the model' : undefined}
                                sx={navigate
                                    ? {color: 'primary.main', cursor: 'pointer', '&:hover': {textDecoration: 'underline'}}
                                    : undefined}
                            >
                                {finding.nodeName ?? '(model-level)'}
                            </Typography>
                            {finding.statementOfTruth && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.25,
                                        pl: 1,
                                        borderLeft: '2px solid',
                                        borderColor: 'divider',
                                        whiteSpace: 'pre-wrap',
                                    }}
                                >
                                    {finding.statementOfTruth}
                                </Typography>
                            )}
                            {finding.detail && (
                                <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic', mt: 0.25}}>
                                    {finding.detail}
                                </Typography>
                            )}
                            {fixable && (
                                <Box sx={{mt: 0.5}}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={fx?.loading}
                                        onClick={(): void => {
                                            void handleSuggestFix(finding);
                                        }}
                                    >
                                        {fx?.loading ? 'Suggesting…' : 'Suggest a fix'}
                                    </Button>
                                    {fx?.error && (
                                        <Typography variant="caption" color="error" sx={{display: 'block', mt: 0.5}}>
                                            {fx.error}
                                        </Typography>
                                    )}
                                    {fx?.suggestion && (
                                        <Box sx={{mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1}}>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                fontWeight="bold"
                                                sx={{display: 'block', mb: 0.5}}
                                            >
                                                Suggested statement of truth (review before applying)
                                            </Typography>
                                            <Typography variant="body2" sx={{whiteSpace: 'pre-wrap', mb: 0.5}}>
                                                {fx.suggestion}
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={(): void => {
                                                    void navigator.clipboard.writeText(fx.suggestion ?? '');
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );

    const renderSeverityPanel = (severity: LintSeverity): ReactElement => {
        const group = bySeverity(severity);
        if (group.length === 0) {
            return (
                <Typography variant="body2" sx={{py: 3, textAlign: 'center'}} color="text.secondary">
                    No {severity.toLowerCase()} findings.
                </Typography>
            );
        }
        const checks = Array.from(new Set(group.map(f => f.check)));
        return (
            <Box sx={{mt: 1}}>
                {checks.map((check: LintCheck): ReactElement =>
                    renderCheckGroup(check, group.filter(f => f.check === check)))}
            </Box>
        );
    };

    return (
        <Box>
            {!started && (
                <Stack alignItems="center" spacing={2} sx={{py: 5}}>
                    <Typography variant="body2" color="text.secondary" sx={{maxWidth: 520, textAlign: 'center'}}>
                        Checks the model's structure and statements of truth for issues that degrade
                        AI report mapping: missing statements, gate semantics, scope holes, and
                        ambiguous boundaries. Structural findings are instant; statement checks
                        follow as they complete.
                    </Typography>
                    <Button variant="contained" onClick={startLint} disabled={scope == null}>
                        Run analysis
                    </Button>
                </Stack>
            )}
            {errorMessage && (
                <Stack spacing={1} sx={{py: 2}}>
                    <Typography color="error" variant="body1">
                        The analysis did not finish, so this model has not been checked.
                    </Typography>
                    <Typography color="error" variant="body2">{errorMessage}</Typography>
                </Stack>
            )}
            {running && !errorMessage && (
                <Box sx={{mb: 2}}>
                    {progress != null
                        ? <LinearProgress variant="determinate" value={progress} />
                        : <LinearProgress />}
                    <Typography variant="caption" color="text.secondary">
                        {snapshot != null && snapshot.llmChecksTotal > 0
                            ? `AI checks: ${snapshot.llmChecksDone} / ${snapshot.llmChecksTotal}`
                            : 'Running checks...'}
                    </Typography>
                </Box>
            )}
            {/* A run that errored is `done` too, and reporting a pass for one would tell a safety
                analyst their model is clean when nothing actually checked it. */}
            {snapshot?.done && !errorMessage && !snapshot.error && findings.length === 0 && (
                <Typography variant="body1" sx={{py: 4, textAlign: 'center'}}>
                    No findings. The model passes all quality checks.
                </Typography>
            )}
            {findings.length > 0 && (
                <Box>
                    <Tabs value={severityTab} onChange={(_, value: number): void => setSeverityTab(value)}>
                        {SEVERITY_ORDER.map((severity: LintSeverity): ReactElement => (
                            <Tab key={severity}
                                label={`${severity} (${bySeverity(severity).length})`}
                                sx={{color: `${SEVERITY_COLOR[severity]}.main`}} />
                        ))}
                    </Tabs>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        {SEVERITY_HELP[SEVERITY_ORDER[severityTab]]}
                    </Typography>
                    {renderSeverityPanel(SEVERITY_ORDER[severityTab])}
                </Box>
            )}
            {finished && (
                <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                    <Button variant="outlined" onClick={startLint} disabled={scope == null}>
                        Re-run analysis
                    </Button>
                </Stack>
            )}
        </Box>
    );
}
